/*
 * Combined region + language selector.
 *
 * Switching works without this file — the panel is a <details> and each option is a submit
 * button. This only adds search filtering, Escape, click-outside, and focus handling.
 */
(() => {
  const normalise = (value) =>
    value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');

  const setup = (root) => {
    if (root.dataset.localeReady) return;
    root.dataset.localeReady = 'true';

    const search = root.querySelector('[data-locale-search]');
    const items = Array.from(root.querySelectorAll('[data-locale-item]'));
    const empty = root.querySelector('[data-locale-empty]');
    const count = root.querySelector('[data-locale-count]');
    // The theme defines this on accessibilityStrings, not variantStrings (js-variables.liquid:75).
    const countTemplate =
      (window.FoxTheme &&
        window.FoxTheme.accessibilityStrings &&
        window.FoxTheme.accessibilityStrings.countrySelectorSearchCount) ||
      '[count] results';

    const filter = () => {
      const term = normalise(search.value.trim());
      let shown = 0;

      items.forEach((item) => {
        const option = item.firstElementChild;
        const name = option.dataset.localeName || '';
        const code = option.dataset.localeCode || '';
        const match = term === '' || normalise(name).includes(term) || code.startsWith(term);
        item.hidden = !match;
        if (match) shown += 1;
      });

      if (empty) empty.hidden = shown !== 0;
      if (count) count.textContent = countTemplate.replace('[count]', shown);
    };

    if (search) {
      // Skip filtering mid-composition: an IME emits partial jamo/kana that matches nothing,
      // so a Korean or Japanese visitor would watch the list blank out on every keystroke.
      search.addEventListener('input', (event) => {
        if (event.isComposing) return;
        filter();
      });
      search.addEventListener('compositionend', filter);

      search.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          // The search box is a control of the country form, so Enter would implicitly submit
          // it via the first submit button — the region already selected — reloading onto the
          // same country and discarding the query. Pick the top match instead.
          if (event.isComposing || event.keyCode === 229) return;
          event.preventDefault();
          const first = items.find((item) => !item.hidden);
          if (first) first.firstElementChild.click();
          return;
        }
        // Escape clears the query first, and only closes the panel when already empty.
        if (event.key !== 'Escape' || search.value === '') return;
        event.stopPropagation();
        search.value = '';
        filter();
      });
    }

    /* ---------------------------------------------------------------------
     * TOP LAYER
     *
     * The panel is absolutely positioned inside the topbar, and the header row
     * below it painted over the panel's top ~110px — hiding the
     * "Country/region" label and the search field entirely (Owen, 2026-08-11).
     * It is not a z-index the panel can win: it is trapped inside the topbar
     * section's own stacking context (position: sticky; z-index: 31), and
     * raising that section, or pushing the following sections down, both
     * changed nothing — verified on the dev theme.
     *
     * So the panel stops competing and leaves the stacking chain entirely.
     * `popover` puts it in the browser's TOP LAYER, which paints above every
     * z-index on the page by definition and cannot be clipped by an ancestor's
     * overflow either — which also protects it from `.site-wrapper`'s
     * `overflow: clip`.
     *
     * `manual` rather than `auto`: this file already owns outside-click,
     * Escape and focusout, and `auto` would add a second light-dismiss that
     * closes the popover while <details> still believes it is open. The panel
     * stays a DOM descendant of <details> even while in the top layer, so
     * `root.contains(event.target)` keeps working unchanged.
     *
     * Position has to be supplied by JS: a popover's UA style is
     * `position: fixed; inset: 0; margin: auto`, i.e. centred, and CSS anchor
     * positioning is not on Safari yet. Everything degrades to the previous
     * absolute positioning where showPopover is unavailable.
     * ------------------------------------------------------------------- */
    const topLayer = (() => {
      const p = root.querySelector('.locale-selector__panel');
      const toggle = root.querySelector('summary');
      if (!p || !toggle || typeof p.showPopover !== 'function') return null;

      /* ⚠ NOT the mobile-menu instance. That one is deliberately a full-width
         panel positioned against .mnav__view so it SCROLLS WITH THE MENU
         (assets/wb-mobile-menu.css). The top layer is `position: fixed` by
         definition, so promoting it would tear it off the drawer and leave it
         floating while the menu scrolled underneath. It has no stacking
         problem to solve either — nothing paints over the inside of an open
         drawer. */
      if (root.closest('.mnav, .mn-fn--locale')) return null;
      p.setAttribute('popover', 'manual');

      const GAP = 8;
      const EDGE = 8;

      const place = () => {
        const r = toggle.getBoundingClientRect();
        const w = p.offsetWidth;
        p.style.position = 'fixed';
        p.style.margin = '0';
        p.style.right = 'auto';

        /* Right-aligned to the toggle, matching the CSS `inset-inline-end: 0`,
           then clamped so a narrow viewport cannot push it off-screen. */
        const left = Math.max(EDGE, Math.min(r.right - w, window.innerWidth - w - EDGE));
        p.style.left = left + 'px';

        if (root.classList.contains('locale-selector--up')) {
          p.style.top = 'auto';
          p.style.bottom = (window.innerHeight - r.top + GAP) + 'px';
          p.style.maxHeight = (r.top - GAP - EDGE) + 'px';
        } else {
          p.style.bottom = 'auto';
          p.style.top = (r.bottom + GAP) + 'px';
          p.style.maxHeight = (window.innerHeight - r.bottom - GAP - EDGE) + 'px';
        }
        p.style.overflowY = 'auto';
      };

      const show = () => {
        if (p.matches(':popover-open')) return;
        try { p.showPopover(); } catch (e) { return; }
        place();
        window.addEventListener('scroll', place, { passive: true });
        window.addEventListener('resize', place);
      };

      const hide = () => {
        window.removeEventListener('scroll', place);
        window.removeEventListener('resize', place);
        if (p.matches(':popover-open')) {
          try { p.hidePopover(); } catch (e) { /* already closed */ }
        }
      };

      return { show, hide };
    })();

    root.addEventListener('toggle', () => {
      if (topLayer) {
        if (root.open) topLayer.show();
        else topLayer.hide();
      }
      if (!root.open) return;
      if (search) {
        search.value = '';
        filter();
        // Only steal focus for pointer-free navigation on comfortable viewports.
        if (window.matchMedia('(min-width: 750px)').matches) search.focus();
      }
      const current = root.querySelector('.locale-selector__option.is-current');
      if (current) current.scrollIntoView({ block: 'nearest' });
    });

    // Choosing a region or language posts and reloads. Show that something is happening,
    // and stop a second click landing while the request is in flight.
    const panel = root.querySelector('.locale-selector__panel');
    let lastSubmitter = null;

    root.addEventListener('click', (event) => {
      const button = event.target.closest('button[type="submit"]');
      if (button) lastSubmitter = button;
    });

    root.querySelectorAll('form').forEach((form) => {
      form.addEventListener('submit', (event) => {
        // event.submitter is unsupported on older Safari, hence the click fallback.
        const button = event.submitter || lastSubmitter;
        if (button) button.classList.add('is-loading');
        if (panel) panel.setAttribute('data-busy', '');
        root.dataset.busy = 'true';
      });
    });

    // Coming back via the back button restores the page from cache mid-spin.
    window.addEventListener('pageshow', () => {
      if (panel) panel.removeAttribute('data-busy');
      root.querySelectorAll('.is-loading').forEach((n) => n.classList.remove('is-loading'));
      delete root.dataset.busy;
    });

    const close = () => {
      if (!root.open || root.dataset.busy) return;
      root.open = false;
    };

    document.addEventListener('click', (event) => {
      if (!root.open || root.contains(event.target)) return;
      close();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !root.open) return;
      close();
      const summary = root.querySelector('summary');
      if (summary) summary.focus();
    });

    root.addEventListener('focusout', (event) => {
      if (!root.open) return;
      if (event.relatedTarget && root.contains(event.relatedTarget)) return;
      if (event.relatedTarget === null) return;
      close();
    });
  };

  const init = () => document.querySelectorAll('[data-locale-selector]').forEach(setup);

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
  init();
})();
